/**
 * InterBranchWidget (iter382) — Dashboard card showing this branch's (or
 * the whole business's) inter-branch net: money paid on behalf of other
 * branches vs money paid by others on our behalf. Clicks through to
 * /branches-balance for the full ledger.
 *
 * iter383 — adds a "Settle Now" button that pops the existing inter-branch
 * settlement endpoint (POST /reports/inter-branch/settle) with a choice of
 * Cash / Bank / Other payment mode.
 */
import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ArrowRightLeft, ArrowDownRight, ArrowUpRight, Banknote, Landmark, Coins, X, Sparkles } from 'lucide-react';
import { RiyalIcon } from '@/lib/currency';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { toast } from 'sonner';

export default function InterBranchWidget({ stats, branches = [], onSettled }) {
  const s = stats || {};
  const net = s.inter_branch_net || 0;
  const owedToUs = s.inter_branch_owed_to_us || 0;
  const weOwe = s.inter_branch_we_owe || 0;
  const [settleOpen, setSettleOpen] = useState(false);

  if (owedToUs === 0 && weOwe === 0) return null;

  const positive = net >= 0;
  // When we OWE others → from=our branch(es), to=others.
  // When others owe US → from=other branches, to=our branch.
  // We don't know which specific pair from stats alone, so the dialog lets
  // the user pick. Amount defaults to |net|.
  return (
    <>
      <Card className="stat-card border-border border-teal-200 bg-gradient-to-br from-teal-50/40 to-orange-50/40" data-testid="inter-branch-card">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">Inter-Branch Balance</CardTitle>
          <div className={`p-2 rounded-lg ${positive ? 'bg-teal-100 text-teal-700' : 'bg-orange-100 text-orange-700'}`}>
            <ArrowRightLeft className="h-5 w-5" strokeWidth={2} />
          </div>
        </CardHeader>
        <CardContent>
          <div className={`text-2xl font-bold font-outfit inline-flex items-center gap-1 ${positive ? 'text-teal-700' : 'text-orange-700'}`} data-testid="inter-branch-net">
            <RiyalIcon size="0.7em" /> {positive ? '+' : '−'}{Math.abs(net).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>
          <div className="text-[11px] text-muted-foreground mt-0.5">{positive ? 'Others owe this branch' : 'This branch owes others'}</div>
          <div className="mt-3 pt-2 border-t border-border/50 space-y-1">
            {owedToUs > 0 && (
              <div className="flex items-center justify-between text-[11px]" data-testid="inter-branch-owed-to-us">
                <span className="text-teal-700 flex items-center gap-1"><ArrowDownRight size={12} />Paid for others</span>
                <span className="font-mono text-teal-700">{owedToUs.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
              </div>
            )}
            {weOwe > 0 && (
              <div className="flex items-center justify-between text-[11px]" data-testid="inter-branch-we-owe">
                <span className="text-orange-700 flex items-center gap-1"><ArrowUpRight size={12} />Paid by others</span>
                <span className="font-mono text-orange-700">−{weOwe.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
              </div>
            )}
          </div>
          <div className="mt-3 flex items-center gap-2">
            <Button size="sm" className="h-7 px-2.5 rounded-lg text-[11px] bg-teal-600 hover:bg-teal-700 text-white" onClick={() => setSettleOpen(true)} data-testid="inter-branch-settle-btn">
              Settle Now
            </Button>
            <Link to="/branches-balance" className="text-[11px] text-info hover:underline" data-testid="inter-branch-open-ledger">Open full ledger →</Link>
          </div>
        </CardContent>
      </Card>

      {settleOpen && (
        <SettleDialog
          defaultAmount={Math.abs(net)}
          direction={positive ? 'owed_to_us' : 'we_owe'}
          branches={branches}
          onClose={() => setSettleOpen(false)}
          onSettled={() => { setSettleOpen(false); onSettled?.(); }}
        />
      )}
    </>
  );
}

function SettleDialog({ defaultAmount, direction, branches, onClose, onSettled }) {
  const [amount, setAmount] = useState(defaultAmount || 0);
  const [mode, setMode] = useState('cash');
  const [fromId, setFromId] = useState('');
  const [toId, setToId] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [suggestion, setSuggestion] = useState(null);
  const [suggesting, setSuggesting] = useState(true);

  // iter384 — auto-pull the top unsettled pair on open so the user can
  // approve in one click. If the backend finds no cross-branch expenses,
  // the dialog falls back to manual entry.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/reports/inter-branch/suggest');
        if (data?.top) {
          setSuggestion(data);
          applySuggestion(data.top);
        }
      } catch { /* silent — manual entry still works */ }
      finally { setSuggesting(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applySuggestion = (s) => {
    setFromId(s.from_branch_id);
    setToId(s.to_branch_id);
    setAmount(s.amount);
    if (s.notes !== undefined) setNotes(s.notes);
  };

  const save = async () => {
    if (!fromId || !toId) { toast.error('Pick both branches'); return; }
    if (fromId === toId)  { toast.error('Branches must differ'); return; }
    if (!amount || amount <= 0) { toast.error('Amount must be > 0'); return; }
    setSaving(true);
    try {
      await api.post('/reports/inter-branch/settle', {
        from_branch_id: fromId,
        to_branch_id: toId,
        amount: Number(amount),
        payment_mode: mode,
        notes,
      });
      toast.success('Settlement recorded');
      onSettled?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to settle');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="settle-dialog">
      <div className="bg-white dark:bg-stone-900 rounded-2xl w-full max-w-md shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-border">
          <div>
            <div className="font-bold">Settle Inter-Branch Balance</div>
            <div className="text-xs text-muted-foreground">{direction === 'owed_to_us' ? 'Another branch is paying you back' : 'You are paying another branch back'}</div>
          </div>
          <button type="button" onClick={onClose} className="text-muted-foreground hover:text-foreground"><X size={16} /></button>
        </div>

        {/* Auto-Match Suggestion banner (iter384) */}
        {suggesting && (
          <div className="mx-5 mt-4 mb-0 px-3 py-2 rounded-lg bg-muted/40 text-[11px] text-muted-foreground flex items-center gap-2" data-testid="settle-suggestion-loading">
            <Sparkles size={12} className="animate-pulse" />
            Finding the top unsettled pair…
          </div>
        )}
        {!suggesting && suggestion?.top && (
          <div className="mx-5 mt-4 mb-0 px-3 py-2 rounded-lg bg-violet-50 dark:bg-violet-950/30 border border-violet-200 dark:border-violet-800 text-[12px]" data-testid="settle-suggestion-banner">
            <div className="flex items-start gap-2">
              <Sparkles size={14} className="text-violet-600 mt-0.5" />
              <div className="flex-1">
                <div className="font-semibold text-violet-800 dark:text-violet-200">Auto-match suggestion</div>
                <div className="text-violet-700 dark:text-violet-300 mt-0.5">
                  <b>{suggestion.top.from_branch_name}</b> → <b>{suggestion.top.to_branch_name}</b> · {Number(suggestion.top.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })} SR
                  <div className="text-[10px] opacity-80">{suggestion.top.transaction_count} cross-branch txn(s) in this period — pre-filled below.</div>
                </div>
                {suggestion.alternatives?.length > 0 && (
                  <details className="mt-1.5">
                    <summary className="text-[10px] cursor-pointer opacity-80 hover:opacity-100">Other pairs ({suggestion.alternatives.length})</summary>
                    <div className="mt-1 space-y-1">
                      {suggestion.alternatives.map((a, i) => (
                        <button
                          key={i}
                          type="button"
                          onClick={() => applySuggestion(a)}
                          className="w-full text-left text-[11px] px-2 py-1 rounded hover:bg-violet-100 dark:hover:bg-violet-900/50"
                          data-testid={`settle-alt-${i}`}
                        >
                          {a.from_branch_name} → {a.to_branch_name} · {Number(a.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })} SR
                        </button>
                      ))}
                    </div>
                  </details>
                )}
              </div>
            </div>
          </div>
        )}
        {!suggesting && !suggestion?.top && (
          <div className="mx-5 mt-4 mb-0 px-3 py-2 rounded-lg bg-muted/40 text-[11px] text-muted-foreground flex items-center gap-2" data-testid="settle-suggestion-empty">
            <Sparkles size={12} />
            No auto-match found — fill manually below.
          </div>
        )}

        <div className="p-5 space-y-3">
          <div>
            <label className="text-[11px] uppercase text-muted-foreground mb-1 block">From Branch (pays cash out)</label>
            <select className="w-full h-9 rounded-lg border border-border bg-background px-2 text-sm" value={fromId} onChange={(e) => setFromId(e.target.value)} data-testid="settle-from-branch">
              <option value="">Select…</option>
              {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[11px] uppercase text-muted-foreground mb-1 block">To Branch (receives cash in)</label>
            <select className="w-full h-9 rounded-lg border border-border bg-background px-2 text-sm" value={toId} onChange={(e) => setToId(e.target.value)} data-testid="settle-to-branch">
              <option value="">Select…</option>
              {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[11px] uppercase text-muted-foreground mb-1 block">Amount (SR)</label>
            <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} className="rounded-lg" data-testid="settle-amount" />
          </div>
          <div>
            <label className="text-[11px] uppercase text-muted-foreground mb-1 block">Payment Mode</label>
            <div className="grid grid-cols-3 gap-2">
              {[
                { k: 'cash',  L: 'Cash',  I: Coins,    c: 'text-success'    },
                { k: 'bank',  L: 'Bank',  I: Landmark, c: 'text-info'       },
                { k: 'other', L: 'Other', I: Banknote, c: 'text-muted-foreground' },
              ].map(({ k, L, I, c }) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => setMode(k)}
                  className={`rounded-lg border p-2 text-sm flex flex-col items-center gap-0.5 transition ${mode === k ? 'border-teal-500 bg-teal-50 text-teal-700 dark:bg-teal-950/30' : 'border-border hover:border-muted-foreground'}`}
                  data-testid={`settle-mode-${k}`}
                >
                  <I size={16} className={mode === k ? '' : c} />
                  <span className="text-[11px] font-semibold">{L}</span>
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-[11px] uppercase text-muted-foreground mb-1 block">Notes (optional)</label>
            <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g. Monthly settlement" className="rounded-lg" data-testid="settle-notes" />
          </div>
        </div>
        <div className="px-5 py-3 border-t border-border flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} className="rounded-lg">Cancel</Button>
          <Button onClick={save} disabled={saving} className="rounded-lg bg-teal-600 hover:bg-teal-700 text-white" data-testid="settle-confirm">
            {saving ? 'Recording…' : 'Record Settlement'}
          </Button>
        </div>
      </div>
    </div>
  );
}
